/**
 * A single collapsible nav group rendered inside the navy sidebar.
 *
 * Header is a button with a chevron affordance (≥44px touch target,
 * aria-expanded). When collapsed, the item list is unmounted. Shared by
 * AdminSidebar and CrmSidebar so the accordion behaviour stays consistent.
 *
 * #2579 (EPIC #2575 Tier 4) — RAIL MODE: when the whole sidebar is collapsed to
 * the desktop icon rail (`collapsed`), this group renders its items as icon-only
 * `RailNavItem` rows with a CSS tooltip; the accordion group HEADER is hidden
 * (there is no room / meaning for it in the rail). A grouped item's rail icon
 * navigates directly to its own landing route (no temporary rail-expand), which
 * keeps the interaction dead simple. Groups are visually separated by a thin
 * divider so the rail still reads as sections.
 *
 * #1934 — the sidebar chrome is ALWAYS dark navy (`bg-navy`) in BOTH light and
 * dark mode, so its active/hover label must be a literal white. We must NOT use
 * Tailwind's `text-white` here: in this codebase `--color-white` is overloaded
 * as a *surface* token and `.dark` redefines it to `#1a2332` (a dark navy), so
 * `text-white` would resolve to dark-on-dark and the label became unreadable on
 * hover in dark mode (the reported bug). `text-[#ffffff]` is a true, theme-
 * independent white that reads correctly against the navy hover background and
 * the blue active background in both themes.
 */
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { RailNavItem } from "./RailNavItem";

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true, the route is "active" only on an exact path match (e.g. dashboards). */
  exact?: boolean;
  /**
   * One short sentence saying what the page is for. Not rendered in the
   * sidebar; it feeds the support assistant's page index (D10-KB-002) via
   * SupportKbCatalog.test.ts. Keep it under ~60 characters.
   */
  purpose?: string;
}

interface NavGroupSectionProps {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: SidebarNavItem[];
  expanded: boolean;
  onToggle: (id: string) => void;
  onNavigate: () => void;
  /** #2579: render as an icon-only rail (desktop collapsed state). */
  collapsed?: boolean;
  /** #2579: hide the top divider on the first rendered group. */
  first?: boolean;
}

function itemIsActive(pathname: string, item: SidebarNavItem): boolean {
  return item.exact ? pathname === item.to : pathname.startsWith(item.to);
}

export function NavGroupSection({
  id,
  label,
  icon: GroupIcon,
  items,
  expanded,
  onToggle,
  onNavigate,
  collapsed = false,
  first = false,
}: NavGroupSectionProps) {
  const location = useLocation();
  const sectionId = `nav-group-${id}`;

  // #2579 RAIL MODE: icon-only, no group header, section divider.
  if (collapsed) {
    return (
      <li>
        {!first && (
          <hr className="my-2 mx-2 border-navy-light" aria-hidden="true" />
        )}
        <ul className="space-y-1" aria-label={label}>
          {items.map((item) => (
            <RailNavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              active={itemIsActive(location.pathname, item)}
              onNavigate={onNavigate}
              dataTour={`nav-${item.to}`}
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
        aria-controls={sectionId}
        className="
          w-full flex items-center gap-3 px-3 min-h-[44px] rounded-lg
          text-xs font-semibold uppercase tracking-wider
          text-gray-400 hover:bg-navy-light hover:text-[#ffffff] transition-colors
        "
      >
        <GroupIcon className="h-4.5 w-4.5 shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
            expanded ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>

      {expanded && (
        <ul id={sectionId} className="mt-1 mb-1 space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = itemIsActive(location.pathname, item);
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  data-tour={`nav-${item.to}`}
                  onClick={onNavigate}
                  className={`
                    flex items-center gap-3 pl-9 pr-3 py-2.5 min-h-[44px] rounded-lg
                    text-sm font-medium transition-colors
                    ${
                      isActive
                        ? "bg-brand-accent text-[#ffffff]"
                        : "text-gray-300 hover:bg-navy-light hover:text-[#ffffff]"
                    }
                  `}
                >
                  <Icon className="h-4.5 w-4.5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
